import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "@/hooks/useTranslation";
import { TeamBillingPriceDTO, UserLicenseChangePreviewDTO } from "@shaarp/common";

interface DialogUserLicenseUpgradePreflightProps {
  preview: UserLicenseChangePreviewDTO;
  targetPrice: TeamBillingPriceDTO;
  onNext: () => void;
  onClose: () => void;
}

/**
 * Step 1 of the Anthropic-inspired user license change flow.
 * Warns that no stock is available and that clicking Next will purchase a new seat.
 */
export const DialogUserLicenseUpgradePreflight = ({
  preview,
  targetPrice,
  onNext,
  onClose,
}: DialogUserLicenseUpgradePreflightProps) => {
  const { t } = useTranslation();

  const isUpgrade = preview.poolAfter.pro > preview.poolBefore.pro;
  const seatCount =
    Math.abs(preview.totalSeatsAfter - preview.totalSeatsBefore) || 1;

  const title = t(
    seatCount === 1
      ? "team.userLicenseChange.preflightTitle_one"
      : "team.userLicenseChange.preflightTitle_other",
    {
      count: seatCount,
      tier: targetPrice.productName,
    }
  );

  const body = isUpgrade
    ? t("team.userLicenseChange.preflightBodyUpgrade", {
        tier: targetPrice.productName,
      })
    : t("team.userLicenseChange.preflightBodyDowngrade");

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[440px] w-full">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <Separator />

        <p className="text-sm text-muted-foreground">{body}</p>

        <Separator />

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("team.userLicenseChange.cancel")}
          </Button>
          <Button onClick={onNext}>
            {t("team.userLicenseChange.next")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
